import { theme } from 'antd'
import { t } from 'i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DetailsPageLayout } from '../../../components'
import {
  ConformityCheckLabQrStatus,
  conformityCheckLabQrStatuses,
} from '../../../constants/constants'
import { ConformityCheckQualityReviewItem } from '../../../store/reducers/workspaceReducer'
import { routePaths } from '../../RootPage'
import ConformityCheckQualityReviewSmartFunctionDetailsPageHeader from './components/ConformityCheckQualityReviewSmartFunctionDetailsPageHeader'
import ConformityCheckQualityReviewSmartFunctionDetailsPagePanel from './panels/ConformityCheckQualityReviewSmartFunctionDetailsPagePanel'
import { useWorkspaceDetails } from '../../../hooks'

const { useToken } = theme

const ConformityCheckQualityReviewSmartFunctionDetailsPage = () => {
  const { token } = useToken()
  const layoutBgColor = token.colorFillAlter
  const { workspaceId, documentId, algorithmRunId } = useParams()
  const navigate = useNavigate()

  const { selectedWorkspace } = useWorkspaceDetails({
    workspaceId,
    preventFetch: true,
  })

  const [filter, setFilter] = useState<ConformityCheckLabQrStatus>(
    conformityCheckLabQrStatuses.ALL
  )
  const [deviations, setDeviations] = useState<
    ConformityCheckQualityReviewItem[] | undefined
  >()

  const backButtonUrl = useMemo(
    () =>
      `${routePaths.WORKSPACE_DETAILS}/${workspaceId}/documents/smartFunction/${documentId}`,
    [workspaceId, documentId]
  )

  const onRunChange = useCallback(
    (algorithmRunId?: string) => {
      const url = `${routePaths.WORKSPACE_SMART_FUNCTION_DETAILS_OFR_QR}/${workspaceId}/${algorithmRunId}/${documentId}`
      navigate(url)
    },
    [workspaceId, documentId, navigate]
  )

  useEffect(() => {
    setDeviations(
      selectedWorkspace?.config?.conformityChecksConfig?.flaggedDeviations
    )
  }, [algorithmRunId, selectedWorkspace])

  const handleDeviationToggle = useCallback(
    (record: ConformityCheckQualityReviewItem, value: boolean) => {
      setDeviations((prev) => {
        const newDeviations = [...(prev || [])]
        if (value) {
          newDeviations.push({
            ...record,
            isDeviation: true,
            algorithmRunId: algorithmRunId || '',
            documentId: documentId || '',
          })
          return newDeviations
        } else {
          return newDeviations.filter((deviation) => deviation.id !== record.id)
        }
      })
    },
    [algorithmRunId, documentId]
  )

  const handleFilterChange = useCallback(
    (value?: ConformityCheckLabQrStatus) => {
      if (value) {
        setFilter(value)
      }
    },
    []
  )

  return (
    <DetailsPageLayout $bgColor={layoutBgColor}>
      <ConformityCheckQualityReviewSmartFunctionDetailsPageHeader
        backButtonUrl={backButtonUrl}
        title={t(
          'workspaceDetailsPage.conformityCheckSmartFunction.qualityReview'
        )}
        onRunChange={onRunChange}
        filter={filter}
        onFilterChange={handleFilterChange}
        deviations={deviations}
      />
      <ConformityCheckQualityReviewSmartFunctionDetailsPagePanel
        filter={filter}
        deviations={deviations}
        onDeviationToggle={handleDeviationToggle}
      />
    </DetailsPageLayout>
  )
}

export default ConformityCheckQualityReviewSmartFunctionDetailsPage
